<main class="grow vendor-accent" role="content">
  <div class="kt-container-fixed vendor-detail-container py-5 lg:py-7.5">
    @if (!vendor()) {
      <div class="vendor-page-head mb-5">
        <div>
          <span class="vendor-eyebrow">Catalog</span>
          <h1 class="vendor-title">Vendor Profile</h1>
        </div>
        <a class="kt-btn kt-btn-outline" [routerLink]="['/catalogs/vendor']">Back to Vendor Directory</a>
      </div>
      <div class="kt-alert kt-alert-destructive">{{ loadError() || 'Vendor not found.' }}</div>
    } @else {
      <div class="vendor-page-head mb-5">
        <div>
          <span class="vendor-eyebrow">Catalog · Vendor Profile</span>
          <h1 class="vendor-title">{{ vendor()!.name }}</h1>
        </div>
        <div class="inline-flex items-center gap-2">
          <a class="kt-btn kt-btn-outline" [routerLink]="['/catalogs/vendor']">Back</a>
          @if (isEditing()) {
            <button type="button" class="kt-btn kt-btn-outline" [disabled]="isSaving()" (click)="cancelEditing()">
              Cancel
            </button>
            <button type="button" class="kt-btn kt-btn-primary" [disabled]="isSaving()" (click)="saveVendor()">
              {{ isSaving() ? 'Saving...' : 'Save Changes' }}
            </button>
          } @else {
            <button type="button" class="kt-btn kt-btn-outline" (click)="startEditing()">Edit Vendor</button>
            <button type="button" class="kt-btn kt-btn-destructive" (click)="deleteVendor()">Delete</button>
          }
        </div>
      </div>

      @if (loadError()) {
        <div class="kt-alert kt-alert-destructive mb-4">{{ loadError() }}</div>
      }
      @if (validationMessage()) {
        <div class="kt-alert kt-alert-destructive mb-4">{{ validationMessage() }}</div>
      }

      <section class="vendor-sheet mb-5">
        <header class="vendor-sheet-banner">
          <div>
            <div class="text-lg font-bold">{{ vendor()!.name }}</div>
            <div class="mt-1 flex items-center gap-2 flex-wrap">
              @if (vendor()!.vendorTypeName) {
                <span class="vendor-type-tag">{{ vendor()!.vendorTypeName }}</span>
              }
              @if (isEditing()) {
                <span class="vendor-editing-chip">Editing</span>
              }
            </div>
          </div>
          <div class="text-end">
            <div class="vendor-field-label">Default Currency</div>
            <div class="text-xl font-bold">{{ vendor()!.defaultCurrency || '-' }}</div>
          </div>
        </header>

        <section class="vendor-section">
          <h2 class="vendor-section-title">Identification</h2>
          @if (isEditing()) {
            <div class="vendor-field-grid">
              <label class="field-block">
                <span class="field-label">Vendor Name *</span>
                <input
                  #nameInput
                  class="kt-input"
                  [value]="form().name"
                  (input)="onFieldChange('name', nameInput.value)"
                />
              </label>
              <label class="field-block">
                <span class="field-label">Vendor Type *</span>
                <select
                  #typeInput
                  class="kt-select"
                  [value]="form().vendorTypeId ?? ''"
                  (change)="onFieldChange('vendorTypeId', typeInput.value ? +typeInput.value : null)"
                >
                  <option value="">Select a vendor type</option>
                  @for (option of vendorTypeOptions(); track option.id) {
                    <option [value]="option.id" [selected]="form().vendorTypeId === option.id">{{ option.name }}</option>
                  }
                </select>
              </label>
              <label class="field-block">
                <span class="field-label">Default Currency *</span>
                <input
                  #ccyInput
                  class="kt-input"
                  maxlength="3"
                  [value]="form().defaultCurrency"
                  (input)="onFieldChange('defaultCurrency', ccyInput.value)"
                />
              </label>
              <label class="field-block vendor-field-grid-wide">
                <span class="field-label">Description</span>
                <textarea
                  #descriptionInput
                  class="kt-input min-h-20"
                  [value]="form().description"
                  (input)="onFieldChange('description', descriptionInput.value)"
                ></textarea>
              </label>
            </div>
          } @else {
            <div class="vendor-field-grid">
              <div>
                <div class="vendor-field-label">Vendor Name</div>
                <div class="vendor-field-value">{{ vendor()!.name }}</div>
              </div>
              <div>
                <div class="vendor-field-label">Vendor Type</div>
                <div class="vendor-field-value">{{ vendor()!.vendorTypeName || '-' }}</div>
              </div>
              <div>
                <div class="vendor-field-label">Default Currency</div>
                <div class="vendor-field-value">{{ vendor()!.defaultCurrency || '-' }}</div>
              </div>
              <div>
                <div class="vendor-field-label">Vendor ID</div>
                <div class="vendor-field-value">#{{ vendor()!.id }}</div>
              </div>
              <div class="vendor-field-grid-wide">
                <div class="vendor-field-label">Description</div>
                <div class="vendor-field-value" [class.vendor-field-value-empty]="!vendor()!.description">
                  {{ vendor()!.description || 'No description provided' }}
                </div>
              </div>
            </div>
          }
        </section>

        <section class="vendor-section">
          <h2 class="vendor-section-title">Contact &amp; Address</h2>
          @if (isEditing()) {
            <div class="vendor-field-grid">
              <label class="field-block">
                <span class="field-label">Address Line 1</span>
                <input
                  #address1Input
                  class="kt-input"
                  [value]="form().addressLine1"
                  (input)="onFieldChange('addressLine1', address1Input.value)"
                />
              </label>
              <label class="field-block">
                <span class="field-label">Address Line 2</span>
                <input
                  #address2Input
                  class="kt-input"
                  [value]="form().addressLine2"
                  (input)="onFieldChange('addressLine2', address2Input.value)"
                />
              </label>
              <label class="field-block">
                <span class="field-label">City</span>
                <input
                  #cityInput
                  class="kt-input"
                  [value]="form().city"
                  (input)="onFieldChange('city', cityInput.value)"
                />
              </label>
              <label class="field-block">
                <span class="field-label">State / Province</span>
                <input
                  #stateInput
                  class="kt-input"
                  [value]="form().stateProvince"
                  (input)="onFieldChange('stateProvince', stateInput.value)"
                />
              </label>
              <label class="field-block">
                <span class="field-label">Postal Code</span>
                <input
                  #postalInput
                  class="kt-input"
                  [value]="form().postalCode"
                  (input)="onFieldChange('postalCode', postalInput.value)"
                />
              </label>
              <label class="field-block">
                <span class="field-label">Country Code</span>
                <input
                  #countryInput
                  class="kt-input"
                  maxlength="3"
                  [value]="form().countryCode"
                  (input)="onFieldChange('countryCode', countryInput.value)"
                />
              </label>
              <label class="field-block">
                <span class="field-label">Phone Number</span>
                <input
                  #phoneInput
                  class="kt-input"
                  [value]="form().phoneNumber"
                  (input)="onFieldChange('phoneNumber', phoneInput.value)"
                />
              </label>
            </div>
          } @else {
            <div class="vendor-field-grid">
              <div class="vendor-field-grid-wide">
                <div class="vendor-field-label">Address</div>
                <div class="vendor-field-value" [class.vendor-field-value-empty]="!formatAddress(vendor()!)">
                  {{ formatAddress(vendor()!) || 'No address on file' }}
                </div>
              </div>
              <div>
                <div class="vendor-field-label">Address Line 1</div>
                <div class="vendor-field-value" [class.vendor-field-value-empty]="!vendor()!.addressLine1">
                  {{ vendor()!.addressLine1 || 'Not set' }}
                </div>
              </div>
              <div>
                <div class="vendor-field-label">Address Line 2</div>
                <div class="vendor-field-value" [class.vendor-field-value-empty]="!vendor()!.addressLine2">
                  {{ vendor()!.addressLine2 || 'Not set' }}
                </div>
              </div>
              <div>
                <div class="vendor-field-label">City</div>
                <div class="vendor-field-value" [class.vendor-field-value-empty]="!vendor()!.city">
                  {{ vendor()!.city || 'Not set' }}
                </div>
              </div>
              <div>
                <div class="vendor-field-label">State / Province</div>
                <div class="vendor-field-value" [class.vendor-field-value-empty]="!vendor()!.stateProvince">
                  {{ vendor()!.stateProvince || 'Not set' }}
                </div>
              </div>
              <div>
                <div class="vendor-field-label">Postal Code</div>
                <div class="vendor-field-value" [class.vendor-field-value-empty]="!vendor()!.postalCode">
                  {{ vendor()!.postalCode || 'Not set' }}
                </div>
              </div>
              <div>
                <div class="vendor-field-label">Country Code</div>
                <div class="vendor-field-value" [class.vendor-field-value-empty]="!vendor()!.countryCode">
                  {{ vendor()!.countryCode || 'Not set' }}
                </div>
              </div>
              <div>
                <div class="vendor-field-label">Phone Number</div>
                <div class="vendor-field-value" [class.vendor-field-value-empty]="!vendor()!.phoneNumber">
                  {{ vendor()!.phoneNumber || 'Not set' }}
                </div>
              </div>
            </div>
          }
        </section>

        <section class="vendor-section">
          <h2 class="vendor-section-title">Billing Concepts</h2>
          @if (isEditing()) {
            <div class="vendor-concept-box">
              @for (type of invoiceTypeOptions(); track type.id) {
                <label class="vendor-concept-item">
                  <input
                    type="checkbox"
                    [checked]="hasInvoiceType(type.id)"
                    (change)="toggleInvoiceType(type.id, $any($event.target).checked)"
                  />
                  <span>{{ type.name }}</span>
                </label>
              }
            </div>
          } @else {
            @if (vendor()!.emittedInvoiceTypes.length > 0) {
              <div class="vendor-chip-row">
                @for (concept of vendor()!.emittedInvoiceTypes; track concept) {
                  <span class="vendor-chip">{{ concept }}</span>
                }
              </div>
            } @else {
              <div class="vendor-field-value vendor-field-value-empty">No billing concepts assigned</div>
            }
          }
        </section>

        <section class="vendor-section">
          <h2 class="vendor-section-title">Billing Activity</h2>
          <div class="vendor-field-grid">
            <div>
              <div class="vendor-field-label">Invoices Recorded</div>
              <div class="vendor-field-value">{{ invoices().length }}</div>
            </div>
            <div>
              <div class="vendor-field-label">Latest Invoice Date</div>
              <div class="vendor-field-value" [class.vendor-field-value-empty]="!vendor()!.latestInvoiceDate">
                {{ vendor()!.latestInvoiceDate ? formatDate(vendor()!.latestInvoiceDate) : 'No invoices yet' }}
              </div>
            </div>
            <div>
              <div class="vendor-field-label">Documents Attached</div>
              <div class="vendor-field-value">{{ vendor()!.documents.length }}</div>
            </div>
          </div>
        </section>

        @if (isEditing()) {
          <section class="vendor-section">
            <h2 class="vendor-section-title">Add Documentation</h2>
            <input
              type="file"
              class="kt-input"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              [disabled]="isSaving()"
              (change)="onAttachmentSelected($any($event.target).files)"
            />
            @if (attachmentName()) {
              <div class="text-xs text-secondary-foreground flex items-center gap-2 mt-2">
                <span>{{ attachmentName() }}</span>
                @if (isSaving()) {
                  <span class="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden="true"></span>
                  <span>Uploading...</span>
                } @else {
                  <button type="button" class="kt-btn kt-btn-xs kt-btn-outline" (click)="clearAttachment()">
                    Remove
                  </button>
                }
              </div>
            } @else {
              <div class="text-xs text-secondary-foreground mt-2">
                Optional. Select a file to upload it when you save.
              </div>
            }
          </section>
        }
      </section>

      <section class="kt-card mb-5">
        <div class="kt-card-header min-h-14">
          <h2 class="kt-card-title">Attached documentation</h2>
        </div>
        <div class="kt-card-content p-0">
          <div class="kt-table-wrapper kt-scrollable">
            <table class="kt-table">
              <thead>
                <tr>
                  <th scope="col">Document Name</th>
                  <th scope="col">Uploaded At</th>
                  <th scope="col">Type</th>
                </tr>
              </thead>
              <tbody>
                @for (document of vendor()!.documents; track document.id) {
                  <tr>
                    <td>
                      @if (document.ezshareLink) {
                        <a class="kt-link" [href]="document.ezshareLink" target="_blank" rel="noopener noreferrer">
                          {{ document.fileName }}
                        </a>
                      } @else {
                        {{ document.fileName }}
                      }
                    </td>
                    <td>{{ document.createdAt ? formatDate(document.createdAt) : '-' }}</td>
                    <td>{{ document.type || 'Vendor Support' }}</td>
                  </tr>
                } @empty {
                  <tr>
                    <td colspan="3" class="text-center text-secondary-foreground py-6">No documents attached.</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section class="kt-card">
        <div class="kt-card-header min-h-14 flex-wrap gap-3">
          <h2 class="kt-card-title">Invoices for this vendor</h2>
          <a
            class="kt-btn kt-btn-sm kt-btn-primary"
            [routerLink]="['/invoices/create']"
            [queryParams]="{ vendorId: vendor()!.id }"
          >
            Add New Invoice
          </a>
        </div>
        <div class="kt-card-content p-0">
          <div class="kt-table-wrapper kt-scrollable">
            <table class="kt-table">
              <thead>
                <tr>
                  <th scope="col">Added At</th>
                  <th scope="col">Invoice #</th>
                  <th scope="col">Concepts</th>
                  <th scope="col">Linked To</th>
                  <th scope="col">Issue Date</th>
                  <th scope="col">Invoice Total</th>
                  <th scope="col">User</th>
                </tr>
              </thead>
              <tbody>
                @for (invoice of invoices(); track invoice.id) {
                  <tr>
                    <td>{{ formatDate(invoice.createdAt) }}</td>
                    <td>
                      <a class="text-primary hover:underline" [routerLink]="['/invoices', invoice.id]">
                        {{ invoice.invoiceNumber }}
                      </a>
                    </td>
                    <td>{{ invoice.invoiceConceptNames.join(', ') || '-' }}</td>
                    <td>
                      @if (invoice.linkType === 'Program' && invoice.linkedProgram) {
                        {{ invoice.linkType }} ({{ invoice.linkedProgram }})
                      } @else {
                        {{ invoice.linkType }}
                      }
                    </td>
                    <td>{{ formatDate(invoice.issueDate) }}</td>
                    <td class="text-end">{{ formatAmount(invoice.currency, invoice.amount) }}</td>
                    <td>{{ invoice.lastModifiedBy || invoice.createdBy || '-' }}</td>
                  </tr>
                } @empty {
                  <tr>
                    <td colspan="7" class="text-center text-secondary-foreground py-6">
                      No invoices found for this vendor.
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      </section>
    }
  </div>
</main>
